import { Fragment } from "react";
import { Card, Title, Text } from "@tremor/react";
import { useHeatmap, type HeatmapCell } from "@/lib/queries";

// Severity 1=low, 2=medium, 3=high, 4=critical. 0 = no alerts.
const SEVERITY_HUE: Record<number, string> = {
  0: "rgba(120, 120, 120, 0.08)",  // empty cell — light grey
  1: "16, 185, 129",                // emerald — low
  2: "245, 158, 11",                // amber — medium
  3: "249, 115, 22",                // orange — high
  4: "239, 68, 68",                 // red — critical
};

function cellStyle(cell: HeatmapCell): React.CSSProperties {
  if (cell.count === 0) return { backgroundColor: SEVERITY_HUE[0] };
  // Saturate intensity at 10 alerts/hour. Floor at 0.35 so sparse cells
  // are still visible against the empty-cell grey.
  const intensity = Math.min(1, 0.35 + cell.count / 12);
  return { backgroundColor: `rgba(${SEVERITY_HUE[cell.severity]}, ${intensity})` };
}

export function AlertHeatmap() {
  const q = useHeatmap(24, 10);
  const data = q.data;

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div>
          <Title>Alert heatmap — last 24h</Title>
          <Text>Top 10 hosts by alert count, bucketed hourly. Cell intensity = alert count; hue = highest severity in that hour.</Text>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <Legend label="low"      color="rgb(16, 185, 129)" />
          <Legend label="medium"   color="rgb(245, 158, 11)" />
          <Legend label="high"     color="rgb(249, 115, 22)" />
          <Legend label="critical" color="rgb(239, 68, 68)"  />
        </div>
      </div>

      {q.isLoading && <div className="mt-6 text-sm text-muted-foreground">Loading…</div>}

      {data && data.rows.length === 0 && (
        <div className="mt-6 rounded-md border border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
          No alerts in the last 24 hours.
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <div
            className="inline-grid gap-px"
            style={{
              gridTemplateColumns: `minmax(140px, max-content) repeat(${data.hours.length}, 18px) auto`,
            }}
          >
            {/* Header row: hour labels */}
            <div />
            {data.hours.map((h, i) => (
              <div
                key={`h${i}`}
                className="text-center text-[9px] tabular-nums text-muted-foreground"
                style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
              >
                {/* Show every 3rd label to avoid clutter. */}
                {i % 3 === 0 ? h : ""}
              </div>
            ))}
            <div className="pl-2 text-[10px] uppercase tracking-wider text-muted-foreground">
              total
            </div>

            {/* Body rows */}
            {data.rows.map((row) => (
              <Fragment key={row.ip}>
                <div className="pr-3 font-mono text-xs leading-[18px]">{row.ip}</div>
                {row.cells.map((c, i) => (
                  <div
                    key={`${row.ip}-${i}`}
                    title={`${row.ip} @ ${data.hours[i]} — ${c.count} alert${c.count === 1 ? "" : "s"}`}
                    className="h-[18px] w-[18px] rounded-sm"
                    style={cellStyle(c)}
                  />
                ))}
                <div className="pl-2 text-right text-xs tabular-nums text-muted-foreground leading-[18px]">
                  {row.total}
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function Legend({ label, color }: { label: string; color: string }) {
  return (
    <div className="flex items-center gap-1">
      <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
      <span>{label}</span>
    </div>
  );
}
